// KartenKnopf.tsx — Karten-Pin neben jeder Anschrift (Anlage und Akte, Haupt-
// und weitere Anschriften). Ein Klick öffnet ein kleines Menü: Google Maps,
// Google Earth und — nur bei deutscher Anschrift — TIM-online NRW, wie am
// Immobilien-Risiko (User 30.09.2026, T-326).
//
// Der Auslöser ist ein AnsichtsKnopf (<span role="button">), kein <button>:
// Ein gesperrtes Formular (`<fieldset disabled>`) schaltet Knöpfe tot — die
// Karte soll auch bei geschlossenem Schloss aufgehen. Das Menü hängt per
// Portal an `body` (position:fixed), damit Karten und Fenster es nicht
// abschneiden. Ohne Anschrift erscheint der Knopf nicht.
//
// Google Earth bewusst OHNE Geokodierung: sonst ginge jede angezeigte Anschrift
// schon beim Laden an die Geocoding-API. Earth öffnet dann die Adresssuche.

import * as React from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import { mergeClasses } from "@fluentui/react-components";
import { useFormPrimitiveStyles } from "../formPrimitives";
import { AnsichtsKnopf } from "../AnsichtsKnopf";
import { aktionsKnopfStil } from "./aktionsKnopfStil";
import { hatAnschrift, mapsUrl, type MapsAnschrift } from "../../../utils/mapsUrl";
import {
  buildGoogleEarthUrl,
  buildTimOnlineUrl,
  formatFullAddress,
  timOnlineMoeglich,
  type AddressParts,
} from "../../../utils/geoLinks";
import { alleFenster, aktivesFenster } from "../../windowManager/browserFenster/fensterRegister";

const KartenIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
    <circle cx="12" cy="9" r="2.6" fill="#fff" />
  </svg>
);

const MapsIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#7d2169" aria-hidden="true">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
    <circle cx="12" cy="9" r="2.6" fill="#fff" />
  </svg>
);

const EarthIcon = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="7" stroke="#7d2169" strokeWidth="1.4" />
    <path
      d="M1.4 8h13.2M8 1.4c2 2.4 3 5 3 6.6s-1 4.2-3 6.6c-2-2.4-3-5-3-6.6s1-4.2 3-6.6z"
      stroke="#7d2169"
      strokeWidth="1.2"
      fill="none"
    />
  </svg>
);

const NrwIcon = (
  <svg width="16" height="18" viewBox="0 0 16 18" fill="none" aria-hidden="true">
    <path d="M2 2h12v8c0 4-3 6-6 7-3-1-6-3-6-7V2z" fill="#c9a24b" stroke="#241522" strokeWidth="1" />
    <path
      d="M5 6c1 0 2 1 2 2v2c0 1-1 2-2 2s-2-1-2-2V8c0-1 1-2 2-2zM11 6c1 0 2 1 2 2v2c0 1-1 2-2 2"
      stroke="#241522"
      strokeWidth="0.8"
      fill="none"
    />
  </svg>
);

/** Die Anschrift in der Form, die die Builder aus `geoLinks` erwarten. */
const alsAdresse = (a: MapsAnschrift): AddressParts => ({
  street: a.strasse,
  postalCode: a.plz,
  city: a.ort,
  country: a.land,
});

export type KartenZiel = { label: string; href: string; icon: React.ReactNode };

/** Die Einträge des Menüs — TIM-online nur bei Deutschland oder leerem Land. */
export function kartenZiele(anschrift: MapsAnschrift): KartenZiel[] {
  const adresse = alsAdresse(anschrift);
  const ziele: KartenZiel[] = [
    { label: "Google Maps", href: mapsUrl(anschrift), icon: MapsIcon },
    { label: "Google Earth", href: buildGoogleEarthUrl(formatFullAddress(adresse)), icon: EarthIcon },
  ];
  if (timOnlineMoeglich(adresse)) {
    ziele.push({ label: "TIM-online (NRW)", href: buildTimOnlineUrl(adresse), icon: NrwIcon });
  }
  return ziele;
}

const BREITE = 220;
const ABSTAND = 4;
const RAND = 8;

const menuStil: React.CSSProperties = {
  position: "fixed",
  zIndex: 2000,
  width: BREITE,
  padding: 6,
  display: "flex",
  flexDirection: "column",
  gap: 2,
  background: "#fff",
  border: "1px solid #ece6ec",
  borderRadius: 12,
  boxShadow: "0 18px 46px -16px rgba(44,10,38,.30), 0 2px 10px rgba(44,10,38,.08)",
  fontFamily: '"Hanken Grotesk", -apple-system, system-ui, sans-serif',
};

function MenuEintrag({ ziel, onWahl }: { ziel: KartenZiel; onWahl: () => void }) {
  const [hervor, setHervor] = React.useState(false);
  return (
    <a
      role="menuitem"
      href={ziel.href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onWahl}
      onMouseEnter={() => setHervor(true)}
      onMouseLeave={() => setHervor(false)}
      onFocus={() => setHervor(true)}
      onBlur={() => setHervor(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 10px",
        borderRadius: 8,
        textDecoration: "none",
        font: '600 14px/1.2 "Hanken Grotesk", -apple-system, system-ui, sans-serif',
        color: hervor ? "#7d2169" : "#241522",
        background: hervor ? "#f8eef5" : "transparent",
        outline: "none",
      }}
    >
      <span style={{ width: 18, display: "inline-flex", justifyContent: "center" }}>{ziel.icon}</span>
      {ziel.label}
    </a>
  );
}

export function KartenKnopf({ anschrift, className }: { anschrift: MapsAnschrift; className?: string }) {
  const s = useFormPrimitiveStyles();
  const [offen, setOffen] = React.useState(false);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);
  const ausloeserRef = React.useRef<HTMLElement | null>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);

  const platzieren = React.useCallback(() => {
    const el = ausloeserRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Rechtsbündig unter dem Pin — der sitzt am rechten Rand des Straßenfelds.
    const left = Math.max(RAND, Math.min(r.right - BREITE, aktivesFenster().innerWidth - BREITE - RAND));
    const hoehe = menuRef.current?.offsetHeight ?? 0;
    const platzUnten = aktivesFenster().innerHeight - r.bottom - RAND;
    const nachOben = hoehe > 0 && platzUnten < hoehe && r.top - RAND > platzUnten;
    const top = nachOben ? Math.max(RAND, r.top - ABSTAND - hoehe) : r.bottom + ABSTAND;
    setPos({ top, left });
  }, []);

  const schliessen = React.useCallback(() => setOffen(false), []);

  // Nach dem Mount steht die Höhe fest — erst dann kann nach oben geklappt werden.
  React.useLayoutEffect(() => {
    if (offen) platzieren();
  }, [offen, platzieren]);

  // Fokus auf den ersten Eintrag, damit die Tastatur das Portal erreicht.
  const fokusGesetzt = React.useRef(false);
  React.useEffect(() => {
    if (!offen) {
      fokusGesetzt.current = false;
      return;
    }
    if (fokusGesetzt.current) return;
    fokusGesetzt.current = true;
    menuRef.current?.querySelector<HTMLElement>("[role=menuitem]")?.focus();
  }, [offen, pos]);

  React.useEffect(() => {
    if (!offen) return;
    const beiKlick = (e: PointerEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || ausloeserRef.current?.contains(t)) return;
      schliessen();
    };
    const beiTaste = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        schliessen();
        ausloeserRef.current?.focus();
      }
    };
    alleFenster.addEventListener("pointerdown", beiKlick);
    alleFenster.addEventListener("keydown", beiTaste);
    alleFenster.addEventListener("resize", platzieren);
    alleFenster.addEventListener("scroll", platzieren, true);
    return () => {
      alleFenster.removeEventListener("pointerdown", beiKlick);
      alleFenster.removeEventListener("keydown", beiTaste);
      alleFenster.removeEventListener("resize", platzieren);
      alleFenster.removeEventListener("scroll", platzieren, true);
    };
  }, [offen, schliessen, platzieren]);

  if (!hatAnschrift(anschrift)) return null;

  return (
    <>
      <AnsichtsKnopf
        label="Auf der Karte ansehen"
        className={mergeClasses(s.aktionsKnopf, className)}
        style={aktionsKnopfStil(true)}
        onClick={(el) => {
          ausloeserRef.current = el;
          setOffen((o) => !o);
        }}
      >
        {KartenIcon}
      </AnsichtsKnopf>
      {offen
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              aria-label="Anschrift ansehen in"
              style={{ ...menuStil, top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}
            >
              {kartenZiele(anschrift).map((z) => (
                <MenuEintrag key={z.label} ziel={z} onWahl={schliessen} />
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

export default KartenKnopf;
